import {Container} from 'flux/utils';
import OverlayStore from '../data/Overlay/OverlayStore';
import TourStore from '../data/Tour/TourStore';
import OverlayActions from '../data/Overlay/OverlayActions';
import EdapEventMethods from 'virtual-tour-viewer/src/modules/EdapEventMethods';
import React from 'react';
import Overlay from '../components/rjs/ui/Overlay/Overlay.react';
import {PropTypes} from 'prop-types';
import LoggingPropTypes from '../Contexts/Logging/PropTypes';
import withLog from '../components/rjs/hoc/WithLog.react';
import {getHash} from '../utils/DomHelper';

let callbackCache = new Map();

function onExit() {
    parent.postMessage('CLOSE_VIRTUAL_TOUR', '*');
    if (window !== parent) {
        /**
         * If we are in an iframe, we need to clean up our window's (in this case,
         * we are a child windowContext) history. This is because the parent
         * document may rely on the content not manipulating page history and unfortunately
         * firefox and chrome treat calls to window.history.go(...) and
         * window.history.back() differently when there are child contexts loaded.
         * Chrome treats the childContext.history as part of the parent.history,
         * even if history.back() is called from the parent's scope. Firefox will
         * instead go back as though the child context did not exist.
         */
        window.history.go(Number.MIN_SAFE_INTEGER);
    } else {
        window.close();
    }
}

function getCallbacks(log) {
    /**
     * Why are we caching the callbacks?
     *
     * In ReactJS, PureComponents make a decision on whether to re-render in
     * each frame based on a shallow comparison of the state and props for that
     * instance of the component.
     *
     * If we are regenerating these call backs every frame as anonymous methods,
     * then any PureComponent will be forced to render on every frame. This is
     * problematic for Components such as the DirectNavigationMenu that we need
     * to log when it renders as part of our EDAP event workflow.
     */
    if (!callbackCache.has(log)) {
        callbackCache.set(log, {
            onSelectGroup: OverlayActions.selectGroup,
            onSelectNavSpace: (sphereId) => {
                OverlayActions.selectSphere(sphereId, EdapEventMethods.spaceClick);
            },
            onSphereSelected: (sphereId) => {
                OverlayActions.selectSphere(sphereId, EdapEventMethods.floorMenuClick);
            },
            onClickHelp: () => {
                OverlayActions.viewOnboardingPage(0);
                OverlayActions.showOnboardingGuide(true);
                log.info('onClickHelp', {
                    pageIndex: 0,
                });
            },
            onOnboardingPage: (automated, transitionSource, pageIndex) => {
                let nextPageIndex = pageIndex + 1;
                OverlayActions.viewOnboardingPage(nextPageIndex);
                log.info('onOnboardingPage', {
                    pageIndex: nextPageIndex,
                    automated,
                    transitionSource,
                });
            },
            onOnboardingDone: (automated, transitionSource, pageIndex) => {
                OverlayActions.showOnboardingGuide(false);
                log.info('onOnboardingDone', {
                    pageIndex,
                    automated,
                    transitionSource,
                });
            },
            onNavMenuOpen: () => {
                log.info('onNavMenuOpen', {});
            },
            onNavMenuClose: (automated: boolean) => {
                log.info('onNavMenuClose', {
                    automated,
                });
            },
            onExit,
        });
    }
    return callbackCache.get(log);
}

class OverlayContainer extends React.Component {
    constructor(props) {
        super(props);
        window.addEventListener('hashchange', (event) => {
            OverlayActions.selectSphere(
                getHash(),
                EdapEventMethods.history,
                this.props.useMediaGateway,
                this.props.hideHotspots
            );
        });
        window.addEventListener('message', (event) => {
            if (event.data === 'CLOSE_VIRTUAL_TOUR' && onExit) {
                onExit();
            }
        });
    }

    static getStores() {
        return [OverlayStore, TourStore];
    }

    static calculateState(prevState, props) {
        let tour = TourStore.getState();
        let overlay = OverlayStore.getState();
        let {log, ...params} = props;
        let callbacks = getCallbacks(log);
        return {
            overlay,
            tour,
            params,
            ...callbacks,
        };
    }

    render() {
        return <Overlay {...this.state} />;
    }
}

OverlayContainer.propTypes = {
    log: PropTypes.shape(LoggingPropTypes),
    useMediaGateway: PropTypes.bool,
    hideHotspots: PropTypes.bool,
};

export default withLog(Container.create(OverlayContainer, {withProps: true}));
